import React, {
    ForwardedRef,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { useRefState, useService } from '@pushly/aqe/lib/hooks'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import {
    isActionStep,
    isSendActionOfType,
    JourneyNode,
    NodeEditorRef,
    NodeEditorSubmitResult,
} from '../../types/journey-nodes'
import type { SendActionStep } from '../../types/journey-nodes'
import type { IActionStep } from '@pushly/models/lib/structs/journeys'
import {
    BlueConicRecommendedNotificationIntegration,
    BlueConicRecommendedNotificationParams,
    isBlueConicRecommendedNotificationParams,
    isVespaRecommendedNotificationParams,
    updateStepConfig,
    VespaRecommendedNotificationParams,
} from '@pushly/models/lib/structs/journeys'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { Alert, Button, Cascader, Divider, Flex, Select, Skeleton, Space, Tabs, Tooltip } from 'antd'
import { antdStaticApi } from '../../../../providers/antd-app-provider/antd-static-api'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import { isJourneyResolvableFeedType } from '@pushly/models/lib/enums/feed-type'
import { GetBlueConicIntegrationResponse, GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PropertiesOf } from '@pushly/models/lib/types'
import { getWrappedMacro, Macro, PlatformIntegrationOwnerType } from '@pushly/models/lib/enums'
import { useJourneyContext } from '../../context'
import { BlueConicRecommendationEditor } from './blueconic/blueconic-recommendation-editor'
import { BLUECONIC_DISPLAY_VALUE, MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED } from './blueconic/constants'
import { VespaRecommendationEditor } from './vespa/vespa-recommendation-editor'
import {
    defaultVespaConfigurationValue,
    MAX_VESPA_RECOMMENDATION_CONFIGS_ALLOWED,
    PUSHLY_AI_CURATOR_DISPLAY_VALUE,
} from './vespa/constants'
import { InfoCircleOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import { DomainIntegrationService } from '../../../../services'
import {
    ContentSource,
    FEED_CONTENT_SOURCE,
    FEED_SOURCE_DISPLAY_VALUE,
    getContentSourceOptions,
    getDefaultRecommendationSource,
    RecommendationSourceAvailability,
    validateRecommendedNotification,
} from './helpers'
import { alphaSort } from '../../../../_utils/array'
import classnames from 'classnames'
import { JourneyBuilderMode } from '../../enums'
import { SendNotificationEditor } from '../send-notification-editor'
import { JourneyAction } from '../../types/journey-context'
import { NotificationVariantModel } from '../../../../models/notification/notification-variant.model'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'
import {
    composeFeedNotificationStep,
    FEED_REQUIRED_MESSAGE,
    FEED_TYPE_UNSUPPORTED_MESSAGE,
    type FeedNotificationParams,
    isFeedSelected,
} from '../send-feed-notification-editor/helpers'
import { useFeedSelection } from '../send-feed-notification-editor/use-feed-selection'
import { FeedSetupTab } from '../send-feed-notification-editor/feed-setup-tab'

import './send-recommended-notification-editor.scss'

/**
 * Unified content-source editor.
 *
 * Handles BOTH `SEND_RECOMMENDED_NOTIFICATION` (BlueConic / Vespa) and
 * `SEND_FEED_NOTIFICATION` action nodes behind a single "Source" dropdown. The
 * selected source is a UI-level {@link ContentSource} spanning the two real
 * recommendation sources plus a `'feed'` option — feed is NOT a member of the
 * models `RecommendationSource` enum, it stays its own action type. On submit
 * the editor resolves `configuration.type` to match the chosen source.
 */
type UnifiedContentSourceEditorProps = {
    node: JourneyNode<IActionStep>
    ref: ForwardedRef<NodeEditorRef>
}

type Option = {
    value: string | number
    label: React.ReactNode
    /** Shown but not selectable — a disabled integration a saved step still points at. */
    disabled?: boolean
    children?: Option[]
}

type ActiveTabKey = 'configuration' | 'notification'

type LocalValue = BlueConicRecommendedNotificationParams | VespaRecommendedNotificationParams

/** Display labels for the known content sources (used when re-inserting the
 * currently-selected source into the options list — see below). */
const CONTENT_SOURCE_LABELS: Record<ContentSource, string> = {
    [RecommendationSource.BLUECONIC]: BLUECONIC_DISPLAY_VALUE,
    [RecommendationSource.VESPA]: PUSHLY_AI_CURATOR_DISPLAY_VALUE,
    [FEED_CONTENT_SOURCE]: FEED_SOURCE_DISPLAY_VALUE,
}

/**
 * Info tooltip shown beside the feed picker in the "Source" well. Clarifies the
 * division of responsibility between the feed and the journey.
 */
const FEED_SOURCE_TOOLTIP =
    'Select the feed that supplies articles for this step. If you need a new source, create it in the Feeds menu first.'

/**
 * True when the given step is a Send Feed Notification action step.
 *
 * A type predicate (delegating to the shared `isSendActionOfType`) rather than a
 * plain boolean, so callers narrow to the feed step and read `feed_id` off
 * `configuration.params` without asserting the params type.
 */
function stepIsFeed(step: IActionStep): step is SendActionStep<JourneyActionType.SEND_FEED_NOTIFICATION> {
    return isSendActionOfType(step, JourneyActionType.SEND_FEED_NOTIFICATION)
}

/**
 * Derive the initial recommendation params (BlueConic/Vespa) from the step. When
 * the step is a feed step, a placeholder BlueConic params is returned so the
 * recommendation branch has something to render if the user switches source; the
 * notification is sourced from the node at submit time so it need not be carried.
 */
function deriveInitialRecommendationParams(step: IActionStep): LocalValue {
    if (stepIsFeed(step)) {
        return new BlueConicRecommendedNotificationParams()
    }

    const params = step.configuration.params
    if (isVespaRecommendedNotificationParams(params)) {
        return params.clone()
    }
    if (isBlueConicRecommendedNotificationParams(params)) {
        return params.clone()
    }
    // Plain JSON from API — discriminate by recommendation_source. `in` narrows the
    // params union (only the recommendation shape declares the field) so no cast is
    // needed; params without the key fall through to the BlueConic parse below.
    if (params && 'recommendation_source' in params && params.recommendation_source === RecommendationSource.VESPA) {
        return VespaRecommendedNotificationParams.parseJsonObject(params)
    }
    return BlueConicRecommendedNotificationParams.parseJsonObject(params)
}

/**
 * Derive the initial content source from the step's action type / params.
 *
 * A step that already names a source keeps it, whether or not the domain could
 * choose that source today. A step naming none but carrying a configuration
 * predates the field and opens on BlueConic. Only a step carrying nothing at all
 * is genuinely new, and resolves to undefined here: which source it should open
 * on depends on the domain's integrations, which are not known yet. The Source select renders its
 * placeholder and stays disabled until {@link resolveDefaultContentSource} fills
 * it in.
 */
function deriveInitialContentSource(step: IActionStep, recParams: LocalValue): ContentSource | undefined {
    if (stepIsFeed(step)) {
        return FEED_CONTENT_SOURCE
    }

    const named = recParams.recommendation_source as ContentSource
    if (named) {
        return named
    }

    // Params carrying a configuration but naming no source predate
    // `recommendation_source`, and BlueConic is the only source that existed then —
    // so the step opens on BlueConic, agreeing with the config editor its params
    // already select. Deferring one of these to the domain default would rebuild the
    // params and discard the configuration it holds.
    const carriesConfiguration =
        (recParams.recommendation_config?.length ?? 0) > 0 ||
        !!(recParams as BlueConicRecommendedNotificationParams).integration?.integration_id

    return carriesConfiguration ? RecommendationSource.BLUECONIC : undefined
}

/**
 * Params matching a content source, carrying the notification across.
 *
 * The config editors are selected by the params' own shape, so a source whose
 * params disagree renders the wrong editor. A step is created with
 * BlueConic-shaped params whatever source it ends up on, hence the rebuild.
 */
function buildParamsForSource(source: ContentSource, previous: LocalValue): LocalValue {
    if (source === RecommendationSource.VESPA && !isVespaRecommendedNotificationParams(previous)) {
        return VespaRecommendedNotificationParams.from({
            recommendation_source: RecommendationSource.VESPA,
            recommendation_config: [defaultVespaConfigurationValue],
            notification: previous.notification,
            notification_id: previous.notification_id as string | undefined,
        })
    }
    return previous
}

export const SendRecommendedNotificationEditor = forwardRef<NodeEditorRef, UnifiedContentSourceEditorProps>(
    ({ node }, ref) => {
        const domainIntegrationSvc = useService(DomainIntegrationService)
        const [state, dispatch] = useJourneyContext()
        const readonly = state.mode === JourneyBuilderMode.READONLY

        const step = node.data.step

        const [loadingIntegrations, setLoadingIntegrations] = useState(false)
        const [integrationsResolved, setIntegrationsResolved] = useState(false)
        const [blueConicSourceOptions, setBlueConicSourceOptions] = useState<Option[]>([])
        const [activeTab, setActiveTab] = useState<ActiveTabKey>('configuration')

        const notifEditorRef = useRef<NodeEditorRef>(null)
        const handleSubmitRef = useRef<(() => Promise<NodeEditorSubmitResult>) | null>(null)
        const validateConfigTabRef = useRef<(() => Promise<boolean>) | null>(null)

        // The integration a saved step already points at. It stays in the picker even
        // once disabled, so a completed journey still reviews as the application and
        // store it actually ran with rather than as raw ids.
        const persistedBlueConicIntegrationId = ((): number | undefined => {
            if (stepIsFeed(step)) return undefined
            const params = step.configuration.params
            return params && 'integration' in params ? params.integration?.integration_id : undefined
        })()

        // The builder resolves domain integrations before any node exists, but an
        // empty map is indistinguishable from a fetch that failed, so this is a
        // starting point rather than an answer — `hasBlueConicSource` below prefers
        // the editor's own resolved list once it lands.
        const contextHasBlueConicIntegration = Array.from(state.dependencies?.integrations?.values() ?? []).some(
            (int) => int.provider === DomainIntegrationProviders.BLUECONIC_REC && int.is_active,
        )

        // Derive the mount-time params + source ONCE. Both values are only ever
        // consumed as initial state, but the derivation constructs/parses model
        // structs — so it runs inside a lazy initializer rather than the render body
        // (useRefState takes a plain value, so it cannot lazily derive its own).
        const [initialDerived] = useState(() => {
            const params = deriveInitialRecommendationParams(step)
            return { params, contentSource: deriveInitialContentSource(step, params) }
        })

        const [localValueRef, setLocalValue] = useRefState<LocalValue>(initialDerived.params)
        const localValue = localValueRef.current

        const [contentSource, setContentSource] = useState<ContentSource | undefined>(initialDerived.contentSource)

        // BlueConic is offered only where there is an application/store a NEW step
        // could point at, so the disabled entries kept for display do not count.
        // Prefer the resolved option list, which reflects this editor's own re-fetch,
        // and fall back to the builder's context map before it resolves.
        const hasBlueConicSource =
            blueConicSourceOptions.some((option) => !option.disabled) || contextHasBlueConicIntegration

        const sourceAvailability: RecommendationSourceAvailability = {
            domainFlags: state.domain.flags,
            hasBlueConicIntegration: hasBlueConicSource,
        }

        // Feed selection state. Only fetches once the Feed source is active so a
        // BlueConic/Vespa step doesn't trigger a feeds request. When the user
        // switches to Feed, `enabled` flips and the fetch runs.
        const initialFeedId = stepIsFeed(step) ? step.configuration.params.feed_id : undefined
        const feedSelection = useFeedSelection(state.domain.id, initialFeedId, {
            enabled: contentSource === FEED_CONTENT_SOURCE,
        })

        const onChange = (change: Partial<PropertiesOf<LocalValue>>) => {
            if (isVespaRecommendedNotificationParams(localValue)) {
                setLocalValue(localValue.clone(change as Partial<PropertiesOf<VespaRecommendedNotificationParams>>))
            } else {
                setLocalValue(localValue.clone(change as Partial<PropertiesOf<BlueConicRecommendedNotificationParams>>))
            }
        }

        const handleContentSourceChange = (next: ContentSource) => {
            if (next === contentSource) return

            // Preserve the notification across source switches. It lives on the
            // recommendation params (localValue) for BlueConic/Vespa and on the
            // node's step params for a feed source. Read through the shared feed
            // params view-model: `notification` / `notification_id` are the two
            // fields every action params shape has in common, so this is a safe
            // view of the node whichever source it currently holds.
            //
            // Assertion retained deliberately: the read is intentionally source-agnostic
            // (the node may hold a BlueConic/Vespa step), so no SEND_FEED_NOTIFICATION
            // guard applies here.
            const nodeParams = node.data.step.configuration.params as FeedNotificationParams
            const preservedNotification =
                contentSource === FEED_CONTENT_SOURCE ? nodeParams.notification : localValueRef.current?.notification
            // `notification_id` is modeled as `number | string` on the send-notification
            // params but `string` on the recommendation params. It is an opaque
            // reference here, so it is carried across UNCHANGED (stringifying would
            // break the id comparisons the journey wizard makes downstream) and narrowed
            // once, rather than casting at each factory call below.
            const preservedNotificationId = (
                contentSource === FEED_CONTENT_SOURCE
                    ? nodeParams.notification_id
                    : localValueRef.current?.notification_id
            ) as string | undefined

            setContentSource(next)

            if (next === RecommendationSource.VESPA) {
                setLocalValue(
                    VespaRecommendedNotificationParams.from({
                        recommendation_source: RecommendationSource.VESPA,
                        recommendation_config: [defaultVespaConfigurationValue],
                        notification: preservedNotification,
                        notification_id: preservedNotificationId,
                    }),
                )
            } else if (next === RecommendationSource.BLUECONIC) {
                setLocalValue(
                    BlueConicRecommendedNotificationParams.from({
                        recommendation_source: RecommendationSource.BLUECONIC,
                        recommendation_config: [],
                        // A BlueConic source starts with no integration chosen (the
                        // Cascader fills it in). The model types `integration` as
                        // required, so an empty placeholder is cast to that type until
                        // the user selects one.
                        integration: {} as BlueConicRecommendedNotificationIntegration,
                        notification: preservedNotification,
                        notification_id: preservedNotificationId,
                    }),
                )
            }
            // next === FEED: feed selection is separate state (useFeedSelection) — the
            // existing selection (if any) is preserved; nothing to reset here.
        }

        /**
         * Switching sources discards the current recommendation configuration
         * (the sources have incompatible config shapes), so confirm first when
         * there's actually work to lose. The Select stays bound to
         * contentSource, which only handleContentSourceChange advances — so
         * cancelling needs no explicit revert, it simply never changes.
         *
         * Compares against contentSource rather than
         * localValue.recommendation_source: on a feed step localValue is the
         * placeholder BlueConic params from deriveInitialRecommendationParams,
         * so the latter would read 'blueconic' and swallow a feed → BlueConic
         * switch as a no-op.
         */
        const handleSourceTypeChange = (next: ContentSource) => {
            if (next === contentSource) return

            // A feed step carries the empty placeholder params, so this reads 0
            // and skips the prompt — correct, a feed step has no recommendation
            // config to lose. Switching away from a configured BlueConic/Vespa
            // step still prompts.
            const existingConfigCount = localValue?.recommendation_config?.length ?? 0
            if (existingConfigCount === 0) {
                handleContentSourceChange(next)
                return
            }

            antdStaticApi.modal.confirm({
                title: 'Change Recommendation Source?',
                content: (
                    <div>
                        Changing the source will discard{' '}
                        {existingConfigCount === 1
                            ? 'the configuration you have set up'
                            : `all ${existingConfigCount} configurations you have set up`}
                        . Your notification content will be kept.
                    </div>
                ),
                okText: 'Yes, Change Source',
                cancelText: 'Cancel',
                onOk: () => handleContentSourceChange(next),
            })
        }

        // ── Submit ─────────────────────────────────────────────────────────────

        const handleFeedSubmit = async (): Promise<NodeEditorSubmitResult> => {
            const selectedFeedId = feedSelection.selectedFeedId
            if (!isFeedSelected(selectedFeedId)) {
                antdStaticApi.message.error(FEED_REQUIRED_MESSAGE)
                return
            }

            // Defense in depth: mirror validateConfigTab so a submit can't bypass the
            // picker's disabled guard (e.g. a pre-existing step referencing an
            // unsupported feed type). Guarded with `selectedOption &&` so it never
            // blocks while options are still loading.
            const selectedOption = feedSelection.feedOptions.find((o) => o.id === selectedFeedId)
            if (selectedOption && !isJourneyResolvableFeedType(selectedOption.type)) {
                antdStaticApi.message.error(FEED_TYPE_UNSUPPORTED_MESSAGE)
                return
            }

            const clearInvalid = !!node.data.uiMeta.invalid
            const innerResult = await notifEditorRef.current?.onSubmit()

            // Source the display-only feed data ({ id, name, type }) so the canvas
            // subtitle shows the feed name immediately. Prefer the loaded feed
            // (guarded to the current selection); fall back to the trimmed option.
            const optionMatch = feedSelection.feedOptions.find((opt) => opt.id === selectedFeedId)
            const loaded = feedSelection.loadedFeed
            const selectedFeed =
                loaded && loaded.id === selectedFeedId
                    ? { id: loaded.id, name: loaded.name, type: loaded.feedType }
                    : optionMatch
                    ? { id: optionMatch.id, name: optionMatch.name, type: optionMatch.type }
                    : undefined

            return composeFeedNotificationStep({
                node,
                feedId: selectedFeedId,
                innerResult,
                clearInvalid,
                feed: selectedFeed,
            })
        }

        const handleRecommendedSubmit = async (): Promise<NodeEditorSubmitResult> => {
            const configurationUpdates = await handleConfigurationSubmit(localValue)
            if (!configurationUpdates) {
                return
            }

            const clearInvalid = !!node.data.uiMeta.invalid

            const existingNotification = node.data.step.configuration.params.notification
            const innerResult = await notifEditorRef.current?.onSubmit()

            // Determine whether the user actually edited the notification.
            // The inner SendNotificationEditor returns undefined when untouched.
            const notificationEdited = !!innerResult
            let notificationResult: typeof existingNotification
            if (innerResult && isActionStep(innerResult.step)) {
                notificationResult = innerResult.step.configuration.params.notification
            } else {
                notificationResult = existingNotification
            }

            if (!notificationResult) {
                return
            }

            const newParams = {
                ...configurationUpdates,
                notification: notificationResult,
                notification_id: notificationEdited ? undefined : configurationUpdates.notification_id,
            }

            // When the source type is being converted (the node currently holds a
            // feed step), it is always a real change — skip the no-op short-circuit
            // below so the action type actually flips to recommended.
            const typeChanged = node.data.step.configuration.type !== JourneyActionType.SEND_RECOMMENDED_NOTIFICATION

            const originalParams = node.data.step.configuration.params
            const notificationUnchanged =
                JSON.stringify(notificationResult) === JSON.stringify(originalParams.notification)

            // Reconstruct the original params as the correct struct type so the
            // config comparison is accurate for both BlueConic and Vespa sources.
            // `in` narrows the params union instead of casting — a feed step's params
            // simply has no recommendation_source and lands in the default branch.
            const originalSource =
                'recommendation_source' in originalParams ? originalParams.recommendation_source : undefined
            let originalForCompare: LocalValue
            switch (originalSource) {
                case RecommendationSource.VESPA:
                    originalForCompare = isVespaRecommendedNotificationParams(originalParams)
                        ? originalParams
                        : VespaRecommendedNotificationParams.parseJsonObject(originalParams)
                    break
                case RecommendationSource.BLUECONIC:
                default:
                    originalForCompare = isBlueConicRecommendedNotificationParams(originalParams)
                        ? originalParams
                        : BlueConicRecommendedNotificationParams.parseJsonObject(originalParams)
                    break
            }

            const configUnchanged = configurationUpdates.equals(originalForCompare, {
                omittingKeys: ['notification', 'notification_id'],
            })

            if (!typeChanged && notificationUnchanged && configUnchanged) {
                return
            }

            // Rebuild the step, re-stamping configuration.type to recommended (a
            // no-op unless converting from a feed step). Downstream keys off
            // configuration.type, so this is the single switch point.
            const newStep = updateStepConfig(node.data.step, {
                type: JourneyActionType.SEND_RECOMMENDED_NOTIFICATION,
                params: newParams,
            } as Partial<typeof node.data.step.configuration>)

            return {
                nodeId: node.id,
                step: newStep,
                clearInvalid,
            }
        }

        const handleSubmit = async (): Promise<NodeEditorSubmitResult> => {
            if (contentSource === FEED_CONTENT_SOURCE) {
                return handleFeedSubmit()
            }
            return handleRecommendedSubmit()
        }

        // Validate the configuration tab before advancing to the notification tab.
        const validateConfigTab = async (): Promise<boolean> => {
            if (contentSource === FEED_CONTENT_SOURCE) {
                if (!isFeedSelected(feedSelection.selectedFeedId)) {
                    antdStaticApi.message.error(FEED_REQUIRED_MESSAGE)
                    return false
                }
                // Block a selected feed whose type a journey can't resolve. Guarded
                // with `selectedOption &&` so it never false-blocks while options are
                // still loading; also catches a pre-existing step that references an
                // unsupported feed (the option is present-but-disabled, so it is found).
                const selectedOption = feedSelection.feedOptions.find(
                    (o) => o.id === feedSelection.selectedFeedId,
                )
                if (selectedOption && !isJourneyResolvableFeedType(selectedOption.type)) {
                    antdStaticApi.message.error(FEED_TYPE_UNSUPPORTED_MESSAGE)
                    return false
                }
                return true
            }

            const [isValid, errorMsg] = validateRecommendedNotification(localValueRef.current!)
            if (!isValid) {
                simpleNotification('error', errorMsg)
                return false
            }
            return true
        }

        // Publish the latest handlers to the refs the imperative handle, the tab
        // navigation and the editor footer all call through. Assigning during render
        // is unsafe (StrictMode double-render / a discarded concurrent render would
        // leave a handler from an uncommitted tree in place), so it happens here on
        // commit instead — with no dep array, so every commit republishes and the
        // handlers can never go stale.
        //
        // Deliberately a LAYOUT effect: useImperativeHandle below is itself
        // layout-phase, and layout effects fire in declaration order, so the refs are
        // populated before the handle is (re)created. The footer effect (passive) and
        // goToTab only read the refs from user-driven callbacks, well after commit.
        useLayoutEffect(() => {
            handleSubmitRef.current = handleSubmit
            validateConfigTabRef.current = validateConfigTab
        })

        // Read through the ref rather than capturing it: the parent may hold the
        // handle across commits, and this keeps `onSubmit` pointed at the current
        // handler regardless of effect ordering.
        useImperativeHandle(ref, () => ({ onSubmit: () => handleSubmitRef.current!() }))

        const goToTab = async (nextTabKey: ActiveTabKey) => {
            if (nextTabKey === activeTab) return
            if (nextTabKey === 'notification') {
                const ok = await validateConfigTabRef.current!()
                if (ok) setActiveTab('notification')
            } else {
                // Back to configuration is always allowed — the final Update Step
                // submit re-validates everything.
                setActiveTab('configuration')
            }
        }

        // Integrations already hydrated by the builder, if any. Depended on by SIZE
        // rather than by identity below: the reducer can hand back a new Map for the
        // same contents, and this effect only cares about the empty → populated
        // transition (that's what decides whether it has to fetch).
        const contextIntegrations = state.dependencies?.integrations
        const contextIntegrationCount = contextIntegrations?.size ?? 0

        useEffect(() => {
            let cancelled = false

            const getRecommendationIntegrations = async () => {
                setLoadingIntegrations(true)
                let integrations: Map<number, GetDomainIntegrationResponse> = contextIntegrations ?? new Map()
                // A failed fetch answers nothing. It must not be mistaken for "this domain
                // has no BlueConic integration", which would pick a source on a lie.
                let integrationsKnown = true

                if (!integrations.size) {
                    const svcRes = await domainIntegrationSvc.fetchAll(state.domain.id)

                    // Superseded (domain changed / integrations arrived) or unmounted —
                    // drop the response instead of writing to dead state.
                    if (cancelled) return

                    integrationsKnown = svcRes.ok

                    if (svcRes.ok && svcRes.data?.length) {
                        integrations = new Map(
                            svcRes.data.map<[number, GetDomainIntegrationResponse]>((resInt) => [
                                resInt.id,
                                GetDomainIntegrationResponse.parseJsonObject(resInt),
                            ]),
                        )
                    }
                }

                const bcOptions = Array.from(integrations.values())
                    .filter(
                        (datum) =>
                            datum.provider === DomainIntegrationProviders.BLUECONIC_REC &&
                            (datum.is_active || datum.id === persistedBlueConicIntegrationId),
                    )
                    .map((datum) => ({
                        label: (datum.data as GetBlueConicIntegrationResponse).name,
                        value: datum.id,
                        // Present for display, not for selection. The stores carry it too:
                        // a Cascader only fires onChange on a leaf, and a disabled parent
                        // does not disable its children, so marking the application alone
                        // would leave the step re-pointable at a deactivated integration.
                        disabled: !datum.is_active,
                        children: Object.keys((datum.data as GetBlueConicIntegrationResponse).stores)
                            .map((store_id) => ({
                                label: ((datum.data as GetBlueConicIntegrationResponse).stores[store_id] as any).name,
                                value: store_id,
                                disabled: !datum.is_active,
                            }))
                            .sort(({ label: aLabel }, { label: bLabel }) => alphaSort(aLabel, bLabel)),
                    }))
                    .sort(({ label: aLabel }, { label: bLabel }) => alphaSort(aLabel, bLabel))

                setBlueConicSourceOptions(bcOptions)
                setLoadingIntegrations(false)
                setIntegrationsResolved(integrationsKnown)

                if (!integrationsKnown) {
                    // Say so rather than quietly choosing. The Source select stays on its
                    // placeholder, which the user can answer themselves.
                    antdStaticApi.message.error(
                        "Could not load this domain's integrations. Please choose a Source, or reload to try again.",
                    )
                }
            }

            getRecommendationIntegrations()

            return () => {
                cancelled = true
                // A superseded/unmounted run must not leave the Source controls stuck
                // in their loading (disabled) state; the next run re-arms it.
                setLoadingIntegrations(false)
            }
            // `domainIntegrationSvc` is intentionally omitted: useService resolves the
            // singleton into a ref, so it is stable for this component's lifetime.
        }, [state.domain.id, contextIntegrationCount, persistedBlueConicIntegrationId])

        /**
         * Choose the starting source for a newly added step, once the integration
         * list has actually resolved.
         *
         * Deferred rather than decided at mount because an empty integration map
         * cannot be told apart from a fetch still in flight or one that failed. A
         * domain carrying both sources — which is what running BlueConic against the
         * Curator as a split test needs — would otherwise open on whichever source
         * the unresolved map implied, silently seeding the wrong arm.
         *
         * Only ever fills an empty selection, which by the time it reaches here means a
         * step carrying nothing at all — see deriveInitialContentSource. A step that
         * names its own source, one carrying a configuration that predates the field,
         * and anything the user has already answered are all left alone; rebuilding
         * params for a resolved source would discard what such a step already holds.
         */
        useEffect(() => {
            if (!integrationsResolved || contentSource) return

            const next = getDefaultRecommendationSource(sourceAvailability)
            if (!next) return

            setContentSource(next as ContentSource)
            setLocalValue(buildParamsForSource(next as ContentSource, localValueRef.current))
            // localValueRef is a ref and the setters are stable; re-running on the
            // value itself would fight the user's own edits.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [integrationsResolved, contentSource, hasBlueConicSource])

        useEffect(() => {
            if (readonly) {
                dispatch({ action: JourneyAction.SET_EDITOR_FOOTER, data: undefined })
                return
            }

            const backLabel = contentSource === FEED_CONTENT_SOURCE ? 'Back: Feed Setup' : 'Back: Configuration'

            dispatch({
                action: JourneyAction.SET_EDITOR_FOOTER,
                data: (
                    <Space>
                        {activeTab === 'notification' && (
                            <Button type="default" onClick={() => setActiveTab('configuration')}>
                                {backLabel}
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={async () => {
                                if (activeTab === 'configuration') {
                                    const ok = await validateConfigTabRef.current!()
                                    if (ok) setActiveTab('notification')
                                    return
                                }

                                const nodeChange = await handleSubmitRef.current!()
                                if (nodeChange) {
                                    dispatch({ action: JourneyAction.ON_NODE_STEP_CHANGE, data: nodeChange })
                                    dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                                }
                            }}
                        >
                            {activeTab === 'notification' ? 'Update Step' : 'Next: Notification'}
                        </Button>
                    </Space>
                ),
            })
        }, [activeTab, readonly, contentSource])

        const isSourceReady = (() => {
            if (isVespaRecommendedNotificationParams(localValue)) return true
            if (isBlueConicRecommendedNotificationParams(localValue)) {
                return !!localValue.integration?.integration_id && !!localValue.integration?.store_id
            }
            return false
        })()

        // The Source section sits above the tabs and stays mounted on both, but
        // changing the source resets the configuration — so it is locked once the
        // user advances to the Notification tab (a disabled-state tooltip explains
        // where to change it).
        const sourceLockedByTab = activeTab === 'notification'

        // Source options: the recommendation sources this domain can configure, plus
        // Feed, which ships everywhere.
        //
        // Two sources are retained beyond that: the one currently selected, and the one
        // the step was saved with. Retaining only the current selection made switching
        // away from an unentitled source a one-way door — the step's own source vanished
        // from the list and could not be chosen back within the editor.
        //
        // Retained entries are spliced into the canonical BlueConic, Vespa, Feed order
        // rather than appended, so a kept source does not render after Feed.
        const sourceOptions = (() => {
            const opts = getContentSourceOptions(sourceAvailability)
            const retained = [contentSource, initialDerived.contentSource]

            for (const source of retained) {
                const known =
                    source === RecommendationSource.BLUECONIC ||
                    source === RecommendationSource.VESPA ||
                    source === FEED_CONTENT_SOURCE
                if (!known || opts.some((o) => o.value === source)) continue
                opts.push({ label: CONTENT_SOURCE_LABELS[source], value: source })
            }

            const order: ContentSource[] = [
                RecommendationSource.BLUECONIC,
                RecommendationSource.VESPA,
                FEED_CONTENT_SOURCE,
            ]
            return opts.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value))
        })()

        const configCount = (() => {
            if (isVespaRecommendedNotificationParams(localValue)) {
                return `${localValue.recommendation_config?.length ?? 0}/${MAX_VESPA_RECOMMENDATION_CONFIGS_ALLOWED}`
            }
            if (isBlueConicRecommendedNotificationParams(localValue)) {
                return `${localValue.recommendation_config?.length ?? 0}/${MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED}`
            }
            return ''
        })()

        // Shared empty state shown whenever there isn't a usable recommendation
        // source to configure yet. Reused by the BlueConic branch below so a params
        // object that claims a BlueConic source but is missing its integration
        // (e.g. legacy/misparsed data) falls back here instead of crashing.
        const recommendationSourceEmptyState = (
            <span>Please select a Recommendation Integration Source to continue configuring recommendations</span>
        )

        const isFeedSource = contentSource === FEED_CONTENT_SOURCE

        const configTabLabel = isFeedSource ? (
            'Feed Setup'
        ) : (
            <div>
                Configurations &nbsp;
                {localValue?.recommendation_source && <span className="size-label">({configCount})</span>}
            </div>
        )

        const configTabChildren = isFeedSource ? (
            <FeedSetupTab domainId={state.domain.id} readonly={readonly} feed={feedSelection} />
        ) : (
            <Well
                className="recommendation-configurations-builder-section"
                mode="ghost"
                padding="none"
                footerMode="hidden"
                title="Recommendation Configurations"
            >
                {loadingIntegrations ? (
                    <Skeleton paragraph={{ rows: 10 }} active loading={loadingIntegrations} />
                ) : !isSourceReady ? (
                    recommendationSourceEmptyState
                ) : isBlueConicRecommendedNotificationParams(localValue) ? (
                    // Only mount the BlueConic sub-editor once a concrete integration
                    // is present. Guarding here (in addition to isSourceReady) means a
                    // BlueConic-shaped params object without an integration can never
                    // reach `integration.integration_id` and crash the render.
                    localValue.integration?.integration_id ? (
                        <BlueConicRecommendationEditor
                            readonly={readonly}
                            domain={state.domain}
                            integration_id={localValue.integration?.integration_id}
                            blueConicRecommendation={localValue}
                            onChange={(change) => onChange(change)}
                            ref={ref}
                        />
                    ) : (
                        recommendationSourceEmptyState
                    )
                ) : isVespaRecommendedNotificationParams(localValue) ? (
                    <VespaRecommendationEditor readonly={readonly} vespaParams={localValue} onChange={onChange} />
                ) : null}
            </Well>
        )

        return (
            <Flex
                vertical
                gap={VERTICAL_WELL_GAP}
                id="send-recommended-notification-editor"
                className="send-recommended-notification-editor"
            >
                {readonly && (
                    <>
                        <Alert type="info" message="You are currently viewing this step in View-Only Mode." showIcon />
                        <Divider type="horizontal" />
                    </>
                )}
                <Well
                    className="recommendation-source-section"
                    variant="ghost"
                    footerMode="hidden"
                    padding="none"
                    title="Source"
                >
                    <Flex vertical gap={12}>
                        <Tooltip
                            title={
                                sourceLockedByTab ? 'Source can only be changed from the configuration tab.' : undefined
                            }
                        >
                            <span style={{ display: 'inline-block' }}>
                                <Select
                                    className={classnames('source-type-select', readonly ? 'view-only' : undefined)}
                                    placeholder="Select a Source"
                                    disabled={loadingIntegrations || readonly || sourceLockedByTab}
                                    value={contentSource || undefined}
                                    onChange={handleSourceTypeChange}
                                    style={{ width: 400 }}
                                    options={sourceOptions}
                                />
                            </span>
                        </Tooltip>

                        {contentSource === RecommendationSource.BLUECONIC && (
                            <Cascader<Option>
                                className={classnames(readonly ? 'view-only' : undefined)}
                                placeholder={
                                    loadingIntegrations ? (
                                        <Loading3QuartersOutlined spin={true} />
                                    ) : (
                                        'Select an Application and Store'
                                    )
                                }
                                disabled={loadingIntegrations || readonly || sourceLockedByTab}
                                classNames={{ popup: { root: 'integration-cascader-popup' } }}
                                value={
                                    isBlueConicRecommendedNotificationParams(localValue) &&
                                    localValue.integration?.integration_id
                                        ? [localValue.integration.integration_id, localValue.integration.store_id]
                                        : undefined
                                }
                                allowClear={false}
                                options={blueConicSourceOptions}
                                expandTrigger="hover"
                                onChange={([integration_id, store_id]) => {
                                    onChange({
                                        recommendation_config: (localValue as BlueConicRecommendedNotificationParams)
                                            .recommendation_config,
                                        recommendation_source: RecommendationSource.BLUECONIC,
                                        integration: BlueConicRecommendedNotificationIntegration.from({
                                            integration_owner_type: PlatformIntegrationOwnerType.DOMAIN,
                                            integration_id: parseInt(String(integration_id), 10),
                                            store_id: String(store_id),
                                        }),
                                        notification: localValue.notification,
                                        notification_id: localValue.notification_id,
                                    } as Partial<PropertiesOf<BlueConicRecommendedNotificationParams>>)
                                }}
                                style={{ width: 400 }}
                            />
                        )}

                        {/* Feed source: the picker sits directly under the source
                            Select (mirroring the BlueConic Cascader above). No separate
                            "Feed" field label — the info tooltip explains the division of
                            responsibility. The required-feed check runs on submit. */}
                        {contentSource === FEED_CONTENT_SOURCE &&
                            (feedSelection.feedError ? (
                                <Alert
                                    type="error"
                                    showIcon
                                    message="Unable to load feeds for this domain. Please try again."
                                />
                            ) : (
                                <Flex align="center" gap={8}>
                                    <Select<number>
                                        className={classnames('feed-select', readonly ? 'view-only' : undefined)}
                                        showSearch
                                        allowClear={false}
                                        loading={feedSelection.loadingFeeds}
                                        disabled={feedSelection.loadingFeeds || readonly || sourceLockedByTab}
                                        placeholder="Select a feed"
                                        value={feedSelection.selectedFeedId}
                                        optionFilterProp="label"
                                        options={feedSelection.feedOptions.map((f) => {
                                            // Feed types the octopus resolve arm can't handle are
                                            // listed but disabled, with the label spelling out WHY
                                            // (kept a string so optionFilterProp="label" search
                                            // keeps working).
                                            const resolvable = isJourneyResolvableFeedType(f.type)
                                            return {
                                                label: resolvable
                                                    ? f.name
                                                    : `${f.name} — not supported in journeys`,
                                                value: f.id,
                                                disabled: !resolvable,
                                            }
                                        })}
                                        onChange={(value) => feedSelection.setSelectedFeedId(value)}
                                        notFoundContent="No feeds are available for this domain."
                                        style={{ width: 400 }}
                                    />
                                    <Tooltip title={FEED_SOURCE_TOOLTIP}>
                                        <span className="feed-source-info" role="img" aria-label={FEED_SOURCE_TOOLTIP}>
                                            <InfoCircleOutlined aria-hidden="true" />
                                        </span>
                                    </Tooltip>
                                </Flex>
                            ))}
                    </Flex>
                </Well>

                <Tabs
                    activeKey={activeTab}
                    onChange={(next: ActiveTabKey) => goToTab(next)}
                    items={[
                        {
                            key: 'configuration',
                            label: configTabLabel,
                            children: configTabChildren,
                        },
                        {
                            key: 'notification',
                            label: 'Notification Setup',
                            children: (
                                <SendNotificationEditor
                                    node={node}
                                    ref={notifEditorRef}
                                    builderProps={{
                                        macroProps: {
                                            categories: {
                                                urlFields: [
                                                    'domain',
                                                    'device',
                                                    'location',
                                                    'article_recommendations',
                                                    'notification',
                                                    'secrets',
                                                    'custom',
                                                ],
                                                messageFields: [
                                                    'location',
                                                    'article_recommendations',
                                                    'secrets',
                                                    'custom',
                                                ],
                                            },
                                            // Scopes source-specific article macros (Tracking URL
                                            // is BlueConic-only) so the macro list follows the
                                            // selected source without this editor having to
                                            // enumerate which macros each source supports.
                                            recommendationSource: localValue?.recommendation_source,
                                            imageMacroProps: {
                                                tag: Macro.ARTICLE_IMAGE,
                                                label: 'Article',
                                            },
                                        },
                                        onInitialVariantInit: (variant: NotificationVariantModel) => {
                                            const content = variant.getContent().getDefaultContent()

                                            // BlueConic uses the click-tracking URL; Vespa and Feed
                                            // both use the plain article URL.
                                            let landingUrl = getWrappedMacro(Macro.ARTICLE_URL)
                                            const title = getWrappedMacro(Macro.ARTICLE_TITLE)
                                            const body = getWrappedMacro(Macro.ARTICLE_DESCRIPTION, {
                                                defaultValue: '',
                                            })
                                            const imageUrl = getWrappedMacro(Macro.ARTICLE_IMAGE)

                                            if (contentSource === RecommendationSource.BLUECONIC) {
                                                landingUrl = getWrappedMacro(Macro.ARTICLE_TRACKING_URL)
                                            }

                                            content.setLandingUrl(landingUrl)
                                            content.setTitle(title)
                                            content.setBody(body)
                                            content.setImageUrl(imageUrl)
                                        },
                                    }}
                                />
                            ),
                        },
                    ]}
                />
            </Flex>
        )
    },
)

async function handleConfigurationSubmit(config: LocalValue) {
    const [isValid, errorMsg] = validateRecommendedNotification(config)
    if (!isValid) {
        simpleNotification('error', errorMsg)
        return null
    }

    return config
}
